import React from "react";
import classnames from "classnames";
import {
  ButtonColour, Typography, TypographyVariants, ButtonSize,
} from "@trussle/external-components";
import kebabCase from "lodash.kebabcase";

import { ImageDataLike } from "gatsby-plugin-image";
import { IContentfulImage } from "../../../types/IContentfulImage";
import { IContentfulBackgroundColour } from "../../../types/IContentfulBackgroundColour";
import { IContentfulRichTextResponse } from "../../../types/IContentfulRichText";
import { ICTA } from "../../../types/ICTA";
import { BigLink } from "../../BigLink";
import { addTracking, CallToActions } from "../../CallToActions";
import { RichText } from "../../RichText";
import { transformRichText } from "../../../helpers/richText";
import { PanelWrapper } from "../../PanelWrapper";

import "./styles.scss";

export interface IBigLinkPanelProps {
  bgColour: IContentfulBackgroundColour;
  title?: string;
  headingType?: string;
  headerAnchor?: string;
  content?: IContentfulRichTextResponse;
  callToActions?: ICTA[];
}

export const BigLinkPanel: React.FC<IBigLinkPanelProps> = (props) => {
  const transformedProps = {
    ...props,
    content: props.content
      ? transformRichText(props.content.raw)
      : null,
  };
  const {
    bgColour, callToActions, title, content, headingType, headerAnchor,
  } = transformedProps;
  const whiteLinks = bgColour === "lime-green" || bgColour === "sky-blue";
  const linkClassName = classnames("bigLinkPanel__bigLink", {
    "bigLinkPanel--link-background-blue": !whiteLinks,
    "bigLinkPanel--link-background-white": whiteLinks,
  });

  const ctasAsLinks = callToActions && callToActions.filter((cta) => cta.isBigLink);
  const ctasOnly = callToActions && callToActions.filter((cta) => !cta.isBigLink);
  const secondaryButtonColour = bgColour === "grey" ? ButtonColour.TertiaryLight : ButtonColour.Secondary;

  return (
    <PanelWrapper backgroundColour={bgColour}>
      <nav className="bigLinkPanel" id={headerAnchor}>
        {(title || content) && (
        <div className="bigLinkPanel__contentContainer">
          {title && (
            <Typography
              {...(title && {
                id: kebabCase(title.toLowerCase()),
              })}
              classes="bigLinkPanel__title"
              variant={headingType as TypographyVariants || "h2"}
            >
              {title}
            </Typography>
          )}
          {content && (
            <RichText
              contrastText={bgColour === "navy"}
              className="bigLinkPanel__content"
              content={content}
            />
          )}
        </div>
        )}
        {ctasAsLinks && (
        <div className="bigLinkPanel__linkContainer">
          {ctasAsLinks.map((link, index) => (
            <BigLink
              key={index}
              classNames={linkClassName}
              url={link.url}
              title={link.title}
              image={link.image as IContentfulImage & ImageDataLike}
            />
          ))}
        </div>
        )}
        {ctasOnly && ctasOnly[0] && (
        <div className="bigLinkPanel__ctas">
          <CallToActions
            alignment="center"
            size={ButtonSize.Large}
            secondaryCtaColour={secondaryButtonColour}
            ctas={addTracking("Big link", ...ctasOnly)}
          />
        </div>
        )}
      </nav>
    </PanelWrapper>
  );
};
